/**
 * Address component module.
 * @module shared/components/Address
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import clone from 'clone';
import styled, {withTheme} from 'styled-components/macro';
import {Dropdown, Row, TextInput} from '../../shared';
import {fill, layoutAttribute} from './cssUtils';
import axiosInstance from '../utils/axios';

const _Wrapper = styled.div`
  display: inline-flex;
  min-width: 210px;
  flex-wrap: wrap;
  ${props => fill(props.width)}

  //noinspection CssInvalidPropertyValue
  height: -moz-fit-content;
  height: fit-content;
  ${layoutAttribute.css};
`;

const _FlexItem = styled.div`
  width: 50%;
  flex-grow: 1;
`;

/**
 * Address component
 * @name Address
 */
const Address = props => {
  const {dataPoint, width, readOnly, disabled, required, onChange, onFocus, layout, isNotControlled} = props;

  const {data: countries} = useSWR('/countries',
    url => axiosInstance.get(url, {params: {sort: '+countryName'}})
      .then(res => {
        const formattedCountries = res.data.map(row => ({label: row.countryName, value: row.countryName}));
        formattedCountries.splice(0, 0, formattedCountries.splice(formattedCountries.findIndex(el => el.label === 'United States'), 1)[0]);
        return formattedCountries;
      }));

  const onSubInputChange = (name, value) => {
    let newData;

    if ((name !== 'zip') || (value.length <= 5)) {
      newData = clone(dataPoint.value) || {};
      newData[name] = (name === 'country') ? value.value : value;
    } else {
      const split = value.split('-');

      newData = clone(dataPoint.value) || {};
      newData.zip = split[0];
      newData.zip4 = split[1].replace(/\D/g, '');
    }

    onChange(dataPoint.name, newData);
  };

  let zipCodeValue;

  if (dataPoint.value && dataPoint.value.zip) {
    zipCodeValue = dataPoint.value.zip + (dataPoint.value.zip4 || '');
  } else {
    zipCodeValue = '';
  }

  const country = (dataPoint.value && dataPoint.value.country) || 'United States';

  return <_Wrapper width={width} layout={layout}>
    <Row justifySubRow={'left'} alignItems={'top'}>
      <TextInput
        title={'Street'}
        type={'Text'}
        placeholder={'Street...'}
        dataPoint={{name: 'address', value: (dataPoint.value && dataPoint.value.address) || ''}}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        onChange={onSubInputChange}
        onFocus={onFocus}
        isNotControlled={isNotControlled}
      />
    </Row>
    <Row justifySubRow={'left'} alignItems={'top'}>
      <TextInput
        title={'City'}
        type={'Text'}
        placeholder={'City...'}
        dataPoint={{name: 'city', value: (dataPoint.value && dataPoint.value.city) || ''}}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        onChange={onSubInputChange}
        onFocus={onFocus}
        isNotControlled={isNotControlled}
      />
    </Row>
    <Row justifySubRow={'left'} alignItems={'top'} wrap={false}>
      <_FlexItem>
        <TextInput
          title={'State'}
          type={'Text'}
          placeholder={'State...'}
          dataPoint={{name: 'state', value: (dataPoint.value && dataPoint.value.state) || ''}}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          onChange={onSubInputChange}
          onFocus={onFocus}
          isNotControlled={isNotControlled}
        />
      </_FlexItem>
      <_FlexItem>
        <TextInput
          title={'Zip Code'}
          type={'zip code full'}
          placeholder={'XXXXX-XXXX'}
          dataPoint={{
            name: 'zip',
            value: zipCodeValue,
          }}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          onChange={onSubInputChange}
          onFocus={onFocus}
          isNotControlled={isNotControlled}
        />
      </_FlexItem>
    </Row>
    <Row justifySubRow={'left'} alignItems={'top'}>
      <Dropdown
        title={'Country'}
        placeholder={'Country...'}
        dataPoint={{name: 'country', value: {label: country, value: {label: country, value: country}}}}
        defaultValue={{label: 'United States', value: 'United States'}}
        options={countries}
        onChange={onSubInputChange}
        isSearchable={true}
        width={fill(null)}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
      />
    </Row>
  </_Wrapper>;
};

Address.propTypes = {
  /**
   * the data to be bound to the parent (address, city, state, zip code, zip code 4, and country)
   * zip code 4 is the four digits following "-" in the zip code input (i.e., [zip code]-[zip code 4])
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.shape({
      address: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      zip4: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      country: PropTypes.string,
    }),
  }).isRequired,
  /**
   * the width of the component
   */
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  layout: layoutAttribute.props,
  /**
   * if true, makes all TextInput components read-only and changes the fill opacity to 0%
   */
  readOnly: PropTypes.bool,
  /**
   * disabled boolean
   */
  disabled: PropTypes.bool,
  /**
   * if true, then all components are required
   */
  required: PropTypes.bool,
  /**
   * the onChange function from parent, required if not read-only
   */
  onChange: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.func}, props, propName, componentName);
    if (props.onChange || props.readOnly) {
      return null;
    }
    return new Error(`If ${componentName} is not read-only then prop ${propName} is required.`);
  },
  /**
   * the onChange function from parent (optional)
   */
  onFocus: PropTypes.func,
  /**
   * toggle to determine if component is controlled (controlled TextInput in MultiInput results in strange behavior)
   */
  isNotControlled: PropTypes.bool,
};

export default withTheme(Address);
